import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../../globals";

import HttpManager from "../../../../services/HttpManager";
import { Loading, apiError, ui } from "../kits/shared";
import { UnificacionesHistorial, UnificarVentana } from "./unificarProductos";
import "./tienda.css";

/*
	Ficha del producto (Esteban, 2026-11-03): se abre al tocar el nombre en Bodegas y en Productos. Foto grande que se
	amplía a pantalla completa con zoom, nombre que se cambia ahí mismo (con permiso de Productos) y la existencia en una
	tabla limpia: bodegas en columnas, tallas/colores en renglones, ceros como guion.
*/

interface Props {
	campusId: number;
	productId: number;
	currency: string;
	/** Bit Crear de la sección Productos: el back pide ese bit para guardar (POST _update). */
	canRename: boolean;
	onClose: () => void;
	/** Para que la lista de atrás se refresque (nombre nuevo, unificación). */
	onRenamed?: (name: string) => void;
}

const overlay: React.CSSProperties = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" };
const section: React.CSSProperties = { ...ui.caption, marginTop: "16px", marginBottom: "6px", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" };
const zero = <span style={{ color: "var(--wa-tenue)" }}>–</span>;

const FichaProducto: React.FC<Props> = ({ campusId, productId, currency, canRename, onClose, onRenamed }) => {
	const http = HttpManager.getInstance().inventoryService;
	const [product, setProduct] = useState<any | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [photoUrl, setPhotoUrl] = useState<string | null>(null);
	const [photoFailed, setPhotoFailed] = useState<boolean>(false);
	const [zoomOpen, setZoomOpen] = useState<boolean>(false);
	const [editing, setEditing] = useState<boolean>(false);
	const [draft, setDraft] = useState<string>("");
	const [saving, setSaving] = useState<boolean>(false);
	const [saveError, setSaveError] = useState<string | null>(null);
	/** Si ya había otro producto con ese nombre, el back lo guarda como "Nombre (2)": se avisa cuál quedó. */
	const [renameNote, setRenameNote] = useState<string | null>(null);
	// Unificar repetidos: solo Super Admin (el back también lo exige).
	const currentUser: any = useSelector((state: RootState) => state.userProfile.value);
	const isSuperAdmin = currentUser?.currentRole === "superAdmin";
	const [merging, setMerging] = useState<boolean>(false);
	const [mergeNote, setMergeNote] = useState<string | null>(null);
	const [reloadKey, setReloadKey] = useState<number>(0);
	/** El producto que se ve: si este se unió a otro, la ficha pasa a mostrar el que se quedó. */
	const [viewId, setViewId] = useState<number>(productId);
	useEffect(() => { setViewId(productId); }, [productId]);

	useEffect(() => {
		let alive = true;
		setError(null);
		http.get(campusId, viewId)
			.then((response) => { if (alive) setProduct(response.data); })
			.catch((requestError) => { if (alive) setError(apiError(requestError, "No se pudo abrir el producto.")); });
		return () => { alive = false; };
	}, [campusId, viewId, reloadKey]);

	/** Después de unificar o deshacer: se vuelve a pedir la ficha y la lista de atrás se refresca. */
	const afterMerge = (message: string, survivorId?: number) => {
		setMerging(false);
		setMergeNote(message);
		if (survivorId && survivorId !== viewId) setViewId(survivorId);
		setReloadKey((key) => key + 1);
		if (onRenamed) onRenamed(product?.name || "");
	};

	// La foto original (no la miniatura de 160 px): aquí se ve en grande y se amplía.
	useEffect(() => {
		// Sin la foto anterior: al pasar al que se quedó o al deshacer, no debe verse la del otro producto.
		setPhotoUrl(null);
		if (!product?.hasPhoto) return;
		let objectUrl: string | null = null;
		let alive = true;
		setPhotoFailed(false);
		http.photoBlob(campusId, product.id, false)
			.then((response) => { if (!alive) return; objectUrl = URL.createObjectURL(response.data); setPhotoUrl(objectUrl); })
			.catch(() => { if (alive) setPhotoFailed(true); });
		return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
	}, [campusId, product?.id, product?.hasPhoto]);

	// Escape cierra lo de más arriba: primero el zoom, luego la edición del nombre, luego la ficha.
	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key !== "Escape" || (event as any).waModalHandled) return;
			(event as any).waModalHandled = true;
			const focused = document.activeElement as HTMLElement | null;
			if (zoomOpen) setZoomOpen(false);
			else if (editing) { if (!saving) setEditing(false); }
			// Escribiendo un motivo o buscando: Escape solo sale del campo, no tira lo escrito.
			else if (focused && ["INPUT", "TEXTAREA", "SELECT"].includes(focused.tagName)) focused.blur();
			else if (merging) return; // la ventana de Unificar maneja su propio Escape
			else onClose();
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [zoomOpen, editing, saving, merging, onClose]);

	const startEdit = () => { setDraft(product.name); setSaveError(null); setRenameNote(null); setEditing(true); };

	const saveName = async () => {
		const name = draft.trim().replace(/\s+/g, " ");
		if (name === "" || name === product.name) { setEditing(false); return; }
		setSaving(true);
		setSaveError(null);
		try {
			const response = await http.update(campusId, product.id, { name }, null);
			const saved = String(response.data?.product?.name || name);
			setProduct(response.data?.product ? { ...product, ...response.data.product } : { ...product, name: saved });
			setRenameNote(saved !== name ? "Ya había otro producto llamado «" + name + "»; este quedó como «" + saved + "»." : null);
			setEditing(false);
			if (onRenamed) onRenamed(saved);
		} catch (requestError) {
			setSaveError(apiError(requestError, "No se pudo cambiar el nombre."));
		} finally {
			setSaving(false);
		}
	};

	return <div style={overlay} onMouseDown={(event) => { if (event.target === event.currentTarget && !saving && !merging) onClose(); }}>
		<div className="card" style={{ width: "100%", maxWidth: "920px", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }} role="dialog" aria-modal="true">
			<div className="card-body" style={{ padding: "18px 20px" }}>
				{error ? <><div className="alert alert-danger" style={{ padding: "8px 12px", fontSize: "14px" }}>{error}</div>
					<div style={{ textAlign: "right" }}><button type="button" className="wti-x" aria-label="Cerrar" onClick={onClose}>×</button></div></>
				: !product ? <Loading />
				: <>
					<div style={{ display: "flex", gap: "20px", flexWrap: "wrap", alignItems: "flex-start" }}>
						{/* Foto grande: tocarla la abre a pantalla completa con zoom. */}
						<div style={{ flex: "0 0 300px", maxWidth: "100%" }}>
							<button type="button" disabled={!photoUrl} onClick={() => setZoomOpen(true)} title={photoUrl ? "Ampliar foto" : undefined}
								style={{ width: "300px", maxWidth: "100%", aspectRatio: "1 / 1", border: "1px solid var(--wa-filo)", borderRadius: "12px", background: "var(--wa-papel-2)", padding: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", cursor: photoUrl ? "zoom-in" : "default", position: "relative" }}>
								{photoUrl ? <img src={photoUrl} alt={product.name} style={{ width: "100%", height: "100%", objectFit: "contain", background: "#fff" }} />
									: <span style={{ fontSize: "13px", color: "var(--wa-tenue)" }}>{!product.hasPhoto ? "Sin foto" : photoFailed ? "No se pudo cargar la foto" : "Cargando foto…"}</span>}
								{photoUrl ? <span style={{ position: "absolute", right: "8px", bottom: "8px", background: "rgba(0,0,0,0.6)", color: "#fff", borderRadius: "999px", padding: "2px 10px", fontSize: "11px" }}>Ampliar</span> : null}
							</button>
						</div>

						<div style={{ flex: "1 1 320px", minWidth: 0 }}>
							<div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
								<div style={{ flex: 1, minWidth: 0 }}>
									{editing ? <div>
										<span style={ui.caption}>Nombre del producto</span>
										<div style={{ display: "flex", gap: "6px", marginTop: "3px", flexWrap: "wrap" }}>
											<input type="text" className="form-control" style={{ ...ui.input, flex: "1 1 220px" }} autoFocus maxLength={255} value={draft} disabled={saving}
												onFocus={(event) => event.target.select()}
												onChange={(event) => setDraft(event.target.value)}
												onKeyDown={(event) => { if (event.key === "Enter") saveName(); }} />
											<button type="button" className={ui.btnPrimary} disabled={saving} title={draft.trim() === "" ? "Escribe el nombre." : undefined}
												style={draft.trim() === "" ? { opacity: 0.55, cursor: "not-allowed" } : undefined}
												onClick={() => { if (draft.trim() === "") { setSaveError("Escribe el nombre del producto."); return; } saveName(); }}>{saving ? "Guardando…" : "Guardar"}</button>
											<button type="button" className={ui.btn} disabled={saving} onClick={() => setEditing(false)}>Cancelar</button>
										</div>
										{(product.variants || []).length ? <div style={{ ...ui.small, marginTop: "4px" }}>Sus tallas y colores toman el nombre nuevo.</div> : null}
										{saveError ? <div className="alert alert-danger" style={{ marginTop: "6px", marginBottom: 0, padding: "6px 10px", fontSize: "13px" }}>{saveError}</div> : null}
									</div>
									: <div style={{ display: "flex", gap: "8px", alignItems: "baseline", flexWrap: "wrap" }}>
										<h4 style={{ margin: 0, fontWeight: 700, overflowWrap: "anywhere" }}>{product.name}</h4>
										{canRename && !product.modelId ? <button type="button" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: "12px" }} onClick={startEdit}>Cambiar Nombre</button> : null}
										{isSuperAdmin && !product.modelId && !product.deprecatedAt && !merging ? <button type="button" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: "12px" }}
											onClick={() => { setMergeNote(null); setMerging(true); }}>Unificar Repetidos</button> : null}
									</div>}
									{renameNote && !editing ? <div className="alert alert-warning" style={{ marginTop: "6px", marginBottom: 0, padding: "6px 10px", fontSize: "13px" }}>{renameNote}</div> : null}
									<div style={{ ...ui.small, fontSize: "12px", marginTop: "4px" }}>
										<span style={{ fontFamily: "monospace" }}>{product.waCode || "—"}</span> · {product.kindLabel}
										{product.barcode ? <> · barras {product.barcode}</> : null}
										{product.deprecatedAt ? <span style={{ color: "var(--wa-oro)", fontWeight: 600 }}> · dado de baja el {product.deprecatedAt}</span> : null}
										{product.soldOutAt && !product.deprecatedAt ? <span className="waAgotado">Agotado</span> : null}
									</div>
								</div>
								<button type="button" className="wti-x" aria-label="Cerrar" title="Cerrar" disabled={saving} onClick={onClose}>×</button>
							</div>

							<div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "14px" }}>
								<div style={ui.kpi}>
									<div style={ui.caption}>Precio</div>
									<div style={{ fontSize: "20px", fontWeight: 700, marginTop: "4px" }}>{product.pricePending
										? <span style={{ color: "var(--wa-oro)", fontSize: "15px" }}>Sin precio{product.suggestedPrice ? <div style={ui.small}>sugerido {currency}{Number(product.suggestedPrice).toFixed(2)}</div> : null}</span>
										: currency + Number(product.basePrice ?? product.price ?? 0).toFixed(2)}</div>
								</div>
								{product.trackStock || (product.variants || []).length ? <div style={ui.kpi}>
									<div style={ui.caption}>Existencia total</div>
									<div style={{ fontSize: "20px", fontWeight: 700, marginTop: "4px" }}>{totalOnHand(product)} <span style={{ fontSize: "12px", fontWeight: 400, color: "var(--wa-apagado)" }}>{product.unit || "pieza"}{totalOnHand(product) === 1 ? "" : "s"}</span></div>
								</div> : null}
							</div>
							{product.description ? <p style={{ fontSize: "13px", marginTop: "12px", marginBottom: 0, whiteSpace: "pre-wrap" }}>{product.description}</p> : null}
						</div>
					</div>

					{mergeNote ? <div className="alert alert-success" style={{ marginTop: "12px", marginBottom: 0, padding: "8px 12px", fontSize: "13px", display: "flex", gap: "10px" }}>
						<span style={{ flex: 1 }}>{mergeNote}</span><button type="button" className="btn-close" aria-label="Cerrar" onClick={() => setMergeNote(null)} />
					</div> : null}
					{merging ? <UnificarVentana campusId={campusId} sobre iniciales={[{ id: product.id, name: product.name, waCode: product.waCode, kind: product.kind,
						onHand: totalOnHand(product), variantCount: (product.variants || []).length, modelId: product.modelId || null }]}
						onClose={() => setMerging(false)} onDone={(message, survivorId) => afterMerge(message, survivorId)} /> : null}

					<Existencia product={product} currency={currency} />

					{(product.version?.components || []).length ? <>
						<div style={section}>Componentes · {product.version.label}</div>
						{product.version.components.map((component: any) => <div key={component.id} style={{ fontSize: "13px", padding: "5px 0", borderBottom: "1px solid var(--wa-filo)" }}>
							{component.name} <b>× {component.quantity}</b>
							{component.substitutes.length ? <span style={ui.small}> · o {component.substitutes.map((s: any) => s.name).join(", ")}</span> : null}
							{component.variantSummary ? <span style={ui.small}> · {component.variantSummary.count} variantes, se eligen al vender o entregar</span>
								: component.sizeAtDelivery ? <span style={ui.small}> · talla al entregar</span> : null}
						</div>)}
					</> : null}

					{(product.courses || []).length ? <>
						<div style={section}>Cursos</div>
						<div style={{ fontSize: "13px" }}>{product.courses.map((c: any) => c.course).join(" · ")}</div>
					</> : null}

					{(product.versions || []).length > 1 ? <>
						<div style={section}>Versiones de las piezas</div>
						{product.versions.map((version: any) => <div key={version.id} style={{ fontSize: "13px", padding: "5px 0", borderBottom: "1px solid var(--wa-filo)" }}>
							<b>{version.label}</b>{version.current ? <span style={{ ...ui.small, color: "var(--wa-verde)" }}> · vigente</span> : null}
							<span style={ui.small}> · desde {version.effectiveFrom || "-"}{version.effectiveTo ? " hasta " + version.effectiveTo : ""} · {(version.components || []).map((c: any) => c.name + " ×" + c.quantity).join(", ")}</span>
						</div>)}
					</> : null}
					{isSuperAdmin ? <UnificacionesHistorial campusId={campusId} productId={product.id} refreshKey={reloadKey} onUndone={(message) => afterMerge(message)} /> : null}
				</>}
			</div>
		</div>
		{zoomOpen && photoUrl ? <ZoomFoto url={photoUrl} alt={product?.name || ""} onClose={() => setZoomOpen(false)} /> : null}
	</div>;
};

const totalOnHand = (product: any): number => (product.variants || []).length
	? product.variants.reduce((sum: number, variant: any) => sum + Number(variant.onHand || 0), 0)
	: (product.stockByWarehouse || []).reduce((sum: number, row: any) => sum + Number(row.onHand || 0), 0);

/** Nombre corto de la bodega para el encabezado: "Bodega Tienda SPS" → "Tienda SPS". */
const shortName = (name: string): string => String(name || "").replace(/^Bodega\s+/i, "");

/**
 * Existencia limpia: una tabla con las bodegas del país en columnas (las que tienen algo primero) y, si hay tallas o
 * colores, un renglón por cada una con su total; los ceros van como guion para que resalte lo que sí hay.
 */
const Existencia: React.FC<{ product: any; currency: string }> = ({ product, currency }) => {
	const variants: any[] = product.variants || [];
	if ((product.availability || []).length) {
		return <>
			<div style={section}>Se pueden armar hoy</div>
			<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: "8px" }}>
				{product.availability.map((row: any) => <div key={row.warehouseId} style={{ border: "1px solid var(--wa-filo)", borderRadius: "10px", padding: "8px 12px", opacity: row.available > 0 ? 1 : 0.7 }}>
					<div style={{ fontSize: "12px", color: "var(--wa-apagado)" }}>{shortName(row.warehouse)}{row.delivers === false ? " · no entrega" : ""}</div>
					<div style={{ fontSize: "20px", fontWeight: 700, color: row.available > 0 ? "var(--wa-tinta)" : "var(--wa-tenue)" }}>{row.available}</div>
					{row.limitedBy ? <div style={ui.tiny}>limita: {row.limitedBy}</div> : null}
				</div>)}
			</div>
		</>;
	}
	if (!variants.length && !product.trackStock) return null;

	const rows: { key: number; label: React.ReactNode; levels: any[]; total: number; price?: string }[] = variants.length
		? variants.map((variant) => ({ key: variant.id, label: variant.label || variant.sizeValue || variant.name, levels: variant.stockByWarehouse || [], total: Number(variant.onHand || 0),
			price: variant.ownPrice ? currency + Number(variant.price).toFixed(2) : undefined }))
		: [{ key: product.id, label: "Existencia", levels: product.stockByWarehouse || [], total: totalOnHand(product) }];
	// Columnas: todas las bodegas del país; las que tienen piezas van primero, en su orden.
	const base: any[] = (rows[0]?.levels || []).map((level: any) => ({ id: level.warehouseId, code: level.code, name: level.name }));
	const columnTotal = (id: number) => rows.reduce((sum, row) => sum + Number((row.levels.find((level: any) => level.warehouseId === id) || {}).onHand || 0), 0);
	const columns = [...base.filter((w) => columnTotal(w.id) !== 0), ...base.filter((w) => columnTotal(w.id) === 0)];
	const cell = (row: typeof rows[number], id: number) => Number((row.levels.find((level: any) => level.warehouseId === id) || {}).onHand || 0);
	const grand = rows.reduce((sum, row) => sum + row.total, 0);
	const th: React.CSSProperties = { ...ui.th, ...ui.right, verticalAlign: "bottom" };

	return <>
		<div style={section}>Existencia por bodega</div>
		<div className="waScrollX" style={{ overflowX: "auto", border: "1px solid var(--wa-filo)", borderRadius: "10px" }}>
			<table style={{ width: "100%", borderCollapse: "collapse" }}>
				<thead><tr>
					<th style={{ ...ui.th, verticalAlign: "bottom" }}>{variants.length ? (variants.some((v) => v.colorValue) ? "Talla / Color" : "Talla") : ""}</th>
					{columns.map((w) => <th key={w.id} style={th} title={w.name}>
						<div style={{ whiteSpace: "normal", maxWidth: "110px", marginLeft: "auto", lineHeight: 1.2 }}>{shortName(w.name)}</div>
					</th>)}
					<th style={{ ...th, color: "var(--wa-tinta)" }}>Total</th>
				</tr></thead>
				<tbody>{rows.map((row) => <tr key={row.key}>
					<td style={{ ...ui.td, fontWeight: 600, whiteSpace: "nowrap" }}>{row.label}{row.price ? <span style={{ ...ui.small, fontWeight: 400 }}> · {row.price}</span> : null}</td>
					{columns.map((w) => { const value = cell(row, w.id); return <td key={w.id} style={{ ...ui.td, ...ui.right, fontVariantNumeric: "tabular-nums", fontWeight: value ? 600 : 400 }}>{value ? value : zero}</td>; })}
					<td style={{ ...ui.td, ...ui.right, fontWeight: 700, fontVariantNumeric: "tabular-nums", background: "var(--wa-papel-2)" }}>{row.total ? row.total : zero}</td>
				</tr>)}</tbody>
				{rows.length > 1 ? <tfoot><tr>
					<td style={{ ...ui.td, fontWeight: 700, borderBottom: "none" }}>Total</td>
					{columns.map((w) => { const value = columnTotal(w.id); return <td key={w.id} style={{ ...ui.td, ...ui.right, fontWeight: 700, borderBottom: "none", fontVariantNumeric: "tabular-nums" }}>{value ? value : zero}</td>; })}
					<td style={{ ...ui.td, ...ui.right, fontWeight: 800, borderBottom: "none", background: "var(--wa-papel-2)", fontVariantNumeric: "tabular-nums" }}>{grand}</td>
				</tr></tfoot> : null}
			</table>
		</div>
	</>;
};

const MIN = 1;
const MAX = 6;

/** Foto a pantalla completa: rueda o pellizco para acercar, arrastrar para mover, doble clic para alternar. */
const ZoomFoto: React.FC<{ url: string; alt: string; onClose: () => void }> = ({ url, alt, onClose }) => {
	// Escala y posición juntas: el zoom mueve las dos a la vez y así nunca se desfasan.
	const [view, setView] = useState<{ s: number; x: number; y: number }>({ s: 1, x: 0, y: 0 });
	const scale = view.s;
	const stage = useRef<HTMLDivElement>(null);
	const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
	const gesture = useRef<{ dist: number; scale: number; last: { x: number; y: number } | null; moved: boolean }>({ dist: 0, scale: 1, last: null, moved: false });

	/** Acerca a la escala `next` dejando quieto el punto (px, py), medido desde el centro de la pantalla. */
	const zoomTo = useCallback((next: number | ((current: number) => number), px = 0, py = 0) => {
		setView((v) => {
			const target = Math.min(MAX, Math.max(MIN, typeof next === "function" ? next(v.s) : next));
			if (target === 1) return { s: 1, x: 0, y: 0 };
			return { s: target, x: px - (px - v.x) * (target / v.s), y: py - (py - v.y) * (target / v.s) };
		});
	}, []);
	const center = (clientX: number, clientY: number) => {
		const box = stage.current?.getBoundingClientRect();
		return box ? { x: clientX - box.left - box.width / 2, y: clientY - box.top - box.height / 2 } : { x: 0, y: 0 };
	};

	// La rueda necesita un listener no pasivo para que no se mueva la página de atrás.
	useEffect(() => {
		const el = stage.current;
		if (!el) return;
		const onWheel = (event: WheelEvent) => {
			event.preventDefault();
			const p = center(event.clientX, event.clientY);
			zoomTo((current) => current * Math.exp(-event.deltaY * 0.0025), p.x, p.y);
		};
		el.addEventListener("wheel", onWheel, { passive: false });
		return () => el.removeEventListener("wheel", onWheel);
	}, [zoomTo]);

	const onPointerDown = (event: React.PointerEvent) => {
		pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
		const list = Array.from(pointers.current.values());
		gesture.current.moved = false;
		if (list.length === 2) {
			gesture.current.dist = Math.hypot(list[0].x - list[1].x, list[0].y - list[1].y);
			gesture.current.scale = view.s;
		}
		gesture.current.last = { x: event.clientX, y: event.clientY };
	};
	const onPointerMove = (event: React.PointerEvent) => {
		if (!pointers.current.has(event.pointerId)) return;
		pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
		const list = Array.from(pointers.current.values());
		if (list.length === 2 && gesture.current.dist > 0) {
			gesture.current.moved = true;
			const mid = center((list[0].x + list[1].x) / 2, (list[0].y + list[1].y) / 2);
			zoomTo(gesture.current.scale * Math.hypot(list[0].x - list[1].x, list[0].y - list[1].y) / gesture.current.dist, mid.x, mid.y);
			return;
		}
		const last = gesture.current.last;
		if (last && view.s > 1) {
			const dx = event.clientX - last.x;
			const dy = event.clientY - last.y;
			if (Math.abs(dx) + Math.abs(dy) > 2) gesture.current.moved = true;
			setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
		}
		gesture.current.last = { x: event.clientX, y: event.clientY };
	};
	const onPointerUp = (event: React.PointerEvent) => {
		pointers.current.delete(event.pointerId);
		const rest = Array.from(pointers.current.values());
		gesture.current.last = rest.length ? rest[0] : null;
		if (rest.length < 2) gesture.current.dist = 0;
	};

	const button: React.CSSProperties = { background: "rgba(255,255,255,0.14)", color: "#fff", border: "1px solid rgba(255,255,255,0.25)", borderRadius: "999px", minWidth: "38px", height: "38px", padding: "0 14px", fontSize: "15px", fontWeight: 600 };

	return <div style={{ position: "fixed", inset: 0, zIndex: 2100, background: "rgba(10,10,14,0.94)", display: "flex", flexDirection: "column" }} role="dialog" aria-modal="true" aria-label="Foto ampliada">
		<div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 14px", color: "#fff", flexWrap: "wrap" }}>
			<div style={{ flex: "1 1 160px", minWidth: 0, fontSize: "14px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{alt}</div>
			<button type="button" style={button} aria-label="Alejar" disabled={scale <= MIN} onClick={() => zoomTo((c) => c / 1.5)}>−</button>
			<span style={{ minWidth: "48px", textAlign: "center", fontSize: "13px", fontVariantNumeric: "tabular-nums" }}>{Math.round(scale * 100)}%</span>
			<button type="button" style={button} aria-label="Acercar" disabled={scale >= MAX} onClick={() => zoomTo((c) => c * 1.5)}>+</button>
			<button type="button" style={button} disabled={scale === 1} onClick={() => zoomTo(1)}>Ajustar</button>
			<button type="button" style={{ ...button, background: "#fff", color: "#111" }} onClick={onClose}>Cerrar</button>
		</div>
		<div ref={stage} style={{ flex: 1, overflow: "hidden", position: "relative", touchAction: "none", cursor: scale > 1 ? "grab" : "zoom-in", userSelect: "none" }}
			onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
			onDoubleClick={(event) => { const p = center(event.clientX, event.clientY); zoomTo(scale > 1 ? 1 : 2.5, p.x, p.y); }}>
			<img src={url} alt={alt} draggable={false}
				style={{ position: "absolute", left: "50%", top: "50%", maxWidth: "calc(100% - 32px)", maxHeight: "calc(100% - 32px)", objectFit: "contain",
					transform: `translate(-50%, -50%) translate(${view.x}px, ${view.y}px) scale(${scale})`, transformOrigin: "center center",
					transition: pointers.current.size ? "none" : "transform 0.12s ease-out", background: "#fff", borderRadius: scale === 1 ? "6px" : 0 }} />
		</div>
		<div style={{ textAlign: "center", color: "rgba(255,255,255,0.6)", fontSize: "12px", padding: "6px 0 12px" }}>Rueda del mouse o pellizca para acercar · arrastra para mover · doble clic para alternar</div>
	</div>;
};

export default FichaProducto;
